import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User as UserIcon, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (isAuthenticated) {
    navigate(location.state?.from || '/', { replace: true });
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim()) return setError('Please enter your first name.');
    if (!email.trim()) return setError('Please enter your email address.');
    if (password.length < 8) return setError('Your password needs to be at least 8 characters long.');
    setLoading(true);
    try {
      await register({ email: email.trim(), password, first_name: firstName.trim() });
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-4 fade-in">
      <div className="bg-white rounded-3xl border-2 border-stone-200 shadow-sm p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">
            Create your free account
          </h1>
          <p className="text-text-muted text-lg">
            Save your progress and open the course on any device.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-semibold text-text mb-2">First name</label>
            <div className="relative">
              <UserIcon className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-firstname"
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="e.g. Margaret"
              />
            </div>
          </div>

          <div>
            <label className="block text-base font-semibold text-text mb-2">Email address</label>
            <div className="relative">
              <Mail className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <p className="text-sm text-text-muted mt-1">
              If you have already paid for the course, use the same email you used at checkout.
            </p>
          </div>

          <div>
            <label className="block text-base font-semibold text-text mb-2">Choose a password</label>
            <div className="relative">
              <Lock className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-14 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                data-testid="signup-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-text-muted hover:text-text"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="signup-error" className="bg-error/10 text-error border border-error/30 rounded-xl p-4 text-base">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="signup-submit"
            className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-6 text-lg font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            Create my account
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-stone-200 text-center">
          <p className="text-text-muted text-base">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="go-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
